/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, Fragment, useCallback, useEffect, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Button, Grid, Typography } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { ExpandLess } from '@material-ui/icons';
import moment from 'moment';
import { useApiError, useAppData, useLoading } from '../../providers';
import { Representative } from './RepresentativesModels';
import { useStyles } from './styles';
import { ActivateRevokeSwitch, InviteRepresentativeDialog, PartnershipCard } from './components';
import { Header, PageTitle, RoundBtnIcon, RepresentativesUserIcon, EditIcon, OverflowTip } from '../../components';
import { useCommonStyles } from '../../styles';
import { AddItemResult, PartnerPartnership } from '../../models';
import { getPartnerPartnerships } from '../IBDashboard';
import { addRepresentatives, assignPartnershipsToRepresentative, getRepresentativesData } from './RepresentativesService';
import { trackAmplitudeData } from '../../services/amplitudeService';
import { getTranslation } from '../../utils';

export const Representatives: FC = () => {
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const { translations, locale } = useAppData();
  const [translation, setTranslation] = useState<any>([]);
  const { enqueueSnackbar } = useSnackbar();
  const { setIsLoading } = useLoading();
  const { addError } = useApiError();
  const [partnerships, setPartnerships] = useState<PartnerPartnership[]>([]);
  const [expandedRepresentative, setExpandedRepresentative] = useState<number | false>(false);
  const [representatives, setRepresentatives] = useState<Representative[]>([]);
  const [openInviteDialogTrigger, setOpenInviteDialogTrigger] = useState<boolean>(false);
  const [selectedRepresentative, setSelectedRepresentative] = useState<Representative>();

  useEffect(() => {
    setTranslation(JSON.parse(translations));
  }, [translations]);

  const getPartnershipsData = useCallback(async () => {
    try {
      const response = await getPartnerPartnerships(true);
      setPartnerships(response.data);
    } catch (error) {
      addError(JSON.stringify(error.response));
    }
  }, [addError]);

  const getRepresentatives = useCallback(async () => {
    try {
      const response = await getRepresentativesData();
      setRepresentatives(response.data);
    } catch (error) {
      addError(JSON.stringify(error.response));
    }
  }, [addError]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await getPartnershipsData();
      await getRepresentatives();
      setIsLoading(false);
    })();
  }, []);

  const handleAddResponse = useCallback(
    async (response: any) => {
      const result = response?.data ?? response;
      switch (result.status) {
        case AddItemResult.Success:
          enqueueSnackbar(result?.message || translation.text_6500, { variant: 'success' });
          trackAmplitudeData('partnerPortal_action_representInviteSent', {
            message: 'Representative invited',
          });
          break;

        case AddItemResult.Failure:
          enqueueSnackbar(result?.message || translation.text_6501, { variant: 'error' });
          break;

        case AddItemResult.PrimaryKeyViolation:
          enqueueSnackbar(result?.message || translation.text_507, { variant: 'error' });
          break;

        case AddItemResult.ValidationError:
          enqueueSnackbar(result?.message || translation.text_508, { variant: 'error' });
          break;
        case AddItemResult.DuplicatedValue:
          enqueueSnackbar(result?.message || translation.text_6502, { variant: 'error' });
          break;
        default:
          break;
      }
    },
    [enqueueSnackbar, translation, addError],
  );

  const addEditRepresentative = useCallback(
    async (values, { setSubmitting }) => {
      setIsLoading(true);
      const entity = { ...values, language: locale };
      try {
        if (values.id) {
          const response = await assignPartnershipsToRepresentative(values.id, values.assignedPartnershipCodes);
          const result = response?.data ?? response;
          switch (result.status) {
            case AddItemResult.Success:
              enqueueSnackbar(result.message ? getTranslation(translation, result.message) : translation.partnerships_assigned_successfully, { variant: 'success' });
              break;
            case AddItemResult.Failure:
              enqueueSnackbar(result.message ? getTranslation(translation, result.message) : translation.text_6510, { variant: 'error' });
              break;

            case AddItemResult.ValidationError:
              enqueueSnackbar(result.message ? getTranslation(translation, result.message) : translation.text_508, { variant: 'error' });
              break;
            default:
              break;
          }
        } else {
          try {
            const response = await addRepresentatives(entity);
            await handleAddResponse(response?.data ?? response);
          } catch (error: any) {
            const result = error?.response?.data;
        
            if (result) {
              await handleAddResponse(result);
            } else {
              addError(JSON.stringify(error.response));
            }
          }
        }
        await getRepresentatives();
      } catch (error) {
        addError(JSON.stringify(error.response));
      } finally {
        setIsLoading(false);
        setOpenInviteDialogTrigger(false);
        setSubmitting(false);
      }
    },
    [addError, setIsLoading, handleAddResponse],
  );

  const handleExpandedAccordionChange = (representativeId: number) => (event: any, isExpanded: boolean) => {
    setExpandedRepresentative(isExpanded ? representativeId : false);
  };

  return (
    <>
      <Header title={translation.text_6514} />
      <Grid container direction="column" className={classes.root}>
        <PageTitle title={translation.text_6514} />
        <Grid container direction="column" className={commonClasses.mainContainer} style={{ gap: 8 }}>
          <Grid item xs={12} className={commonClasses.contentWithAccordion}>
            {representatives.map((representative) => (
              <Fragment key={representative.id}>
                <Accordion classes={{ root: commonClasses.accordion }} expanded={expandedRepresentative === representative.id} onChange={handleExpandedAccordionChange(representative.id)}>
                  <AccordionSummary classes={{ content: commonClasses.accordionContent }} expandIcon={<ExpandLess />}>
                    <Grid item>
                      <RepresentativesUserIcon />
                    </Grid>
                    <Grid container direction="column" style={{ marginInlineStart: 10 }}>
                      <Typography className={commonClasses.accordionHeader}>{representative.displayName}</Typography>
                      <Typography className={classes.accordionSubHeader}>
                        <OverflowTip interactive align="start" title={representative.email}>
                          {representative.email}
                        </OverflowTip>
                      </Typography>
                    </Grid>
                    <ActivateRevokeSwitch translation={translation} representative={representative} onSwitchChange={() => getRepresentatives()} />
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid item xs={12}>
                      <Grid container direction="column" justify="space-between">
                        <Grid item xs={12}>
                          <Grid container direction="column" className={classes.email}>
                            <Grid item>
                              <Typography className={classes.detailsTitle}>{translation.text_6696}</Typography>
                            </Grid>
                            <Grid item>
                              <Typography className={classes.detailsItem}>
                                <OverflowTip interactive align="left" title={representative.email}>
                                  {representative.email}
                                </OverflowTip>
                              </Typography>
                            </Grid>
                          </Grid>
                          <Grid container direction="row" justify="space-between">
                            <Grid container direction="column" className={classes.details}>
                              <Grid item>
                                <Typography className={classes.detailsTitle}>{translation.text_6537}</Typography>
                              </Grid>
                              <Grid item>
                                <Typography className={classes.detailsItem}>{moment(representative.invitationDate).format('DD/MM/YYYY')}</Typography>
                              </Grid>
                            </Grid>
                            <Grid container direction="column" className={classes.details}>
                              <Grid item>
                                <Typography className={classes.detailsTitle}>{translation.text_115}</Typography>
                              </Grid>
                              <Grid item>
                                <Grid container direction="row" justify="flex-start" alignItems="center" wrap="wrap">
                                  {representative.representativeAssignedPartnerships?.map((partnership) => (
                                    <PartnershipCard partnership={partnership} />
                                  ))}
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item xs={12} style={{ textAlign: 'left' }}>
                          <Button
                            startIcon={<EditIcon />}
                            className={commonClasses.editBtn}
                            onClick={() => {
                              setOpenInviteDialogTrigger(true);
                              setSelectedRepresentative(representative);
                            }}
                          >
                            {translation.text_471}
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Fragment>
            ))}
          </Grid>
          <Grid item xs={12}>
            <Grid container direction="row" alignItems="center" style={{ padding: 16, height: 88 }}>
              <Grid item>
                <Button
                  onClick={() => {
                    setOpenInviteDialogTrigger(true);
                    setSelectedRepresentative(undefined);
                  }}
                  style={{ padding: 0, minWidth: '100%' }}
                >
                  <RoundBtnIcon />
                </Button>
              </Grid>
              <Grid item style={{ width: '70%', marginInlineStart: 8 }}>
                <Grid container direction="column">
                  <Typography className={commonClasses.accordionHeader}>{translation.inviteRepresentative}</Typography>
                  {/* <Grid container direction="row" alignItems="center"> */}
                  <Typography className={commonClasses.accordionSubHeader}>{translation.inviteRepresentativeTooltip}</Typography>
                </Grid>
              </Grid>
              {/* </Grid> */}
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <InviteRepresentativeDialog
        key={Math.random()}
        open={openInviteDialogTrigger}
        representative={selectedRepresentative}
        partnerships={partnerships}
        onSubmit={addEditRepresentative}
        onCancel={() => {
          setOpenInviteDialogTrigger(false);
          setSelectedRepresentative(undefined);
        }}
        translation={translation}
      />
    </>
  );
};
